<template>
  <div
    class="ws-card"
    :class="[`ws-card--${mode}`]"
    @click="handleCardClick"
  >
    <div class="ws-header">
      <div class="ws-name-block">
        <span class="ws-name" :title="deviceName">
          {{ truncatedName }}
        </span>
        <star-filled v-if="device.starred" class="ws-star" />
      </div>
      <span class="ws-mode-chip">
        <span class="ws-mode-dot" />
        {{ modeLabel }}
      </span>
    </div>

    <div class="ws-body">
      <template v-if="mode === 'guidance'">
        <div class="ws-primary">
          <div class="ws-primary-value">{{ cyclesToday || '—' }}</div>
          <div class="ws-primary-label">Cycles today</div>
        </div>
      </template>

      <template v-else-if="mode === 'recording'">
        <div class="ws-primary">
          <div class="ws-primary-value">{{ hoursRecorded || '—' }}</div>
          <div class="ws-primary-label">Recorded today</div>
        </div>
      </template>

      <template v-else-if="mode === 'idle'">
        <div class="ws-primary ws-primary--muted">
          <div class="ws-primary-value">Idle</div>
          <div class="ws-primary-label">Awaiting activity</div>
        </div>
      </template>

      <template v-else>
        <div class="ws-primary ws-primary--muted">
          <div class="ws-primary-value">Offline</div>
          <div class="ws-primary-label">
            Last seen: {{ device.last_Active || '—' }}
          </div>
        </div>
      </template>
    </div>

    <div class="ws-footer">
      <div class="ws-task" :title="currentTaskName">
        <span v-if="currentTaskName" class="ws-task-name">
          {{ currentTaskName }}
        </span>
        <span v-else class="ws-task-muted">No operation</span>
      </div>

      <div v-if="mode === 'guidance'" class="ws-kpis">
        <span class="ws-kpi" title="Bad-cycle rate">
          <span class="ws-kpi-value">{{ badPct }}</span>
          <span class="ws-kpi-label">bad</span>
        </span>
        <span class="ws-kpi" title="Takt grade">
          <span class="ws-kpi-value">{{ taktGrade || '—' }}</span>
          <span class="ws-kpi-label">takt</span>
        </span>
        <span
          v-if="hasValueStoryAccess && hasValueStoryConfig"
          class="ws-kpi ws-kpi--roi"
          title="Rough cost avoided today"
        >
          <span class="ws-kpi-value">{{ formatMoney(costAvoided) }}</span>
          <span class="ws-kpi-label">saved</span>
        </span>
      </div>

      <div
        v-if="mode === 'guidance' && topMissedSteps.length"
        class="ws-missed"
      >
        <div class="ws-missed-label">Top missed</div>
        <div
          v-for="(step, i) in topMissedSteps"
          :key="i"
          class="ws-missed-row"
          :title="step.step_name"
        >
          <span class="ws-missed-rank">{{ i + 1 }}</span>
          <span class="ws-missed-name">{{ step.step_name }}</span>
          <span class="ws-missed-count">{{ step.count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { StarFilled } from '@ant-design/icons-vue';
import { roughCostAvoided } from 'src/utils/valueStory/enrichSteps';
import { mapGetters } from 'vuex';
import { formatHoursRecorded } from './hoursRecorded';

const MODE_LABELS = {
  guidance: 'Guidance',
  recording: 'Recording',
  idle: 'Idle',
  offline: 'Offline',
};

export default {
  components: { StarFilled },
  props: {
    device: { type: Object, required: true },
    activeDevices: { type: Object, required: true, default: () => ({}) },
    activeDevicesStatus: { type: Object, required: true, default: () => ({}) },
    idToTaskNameMapping: { type: Object, required: true, default: () => ({}) },
    valueStoryConfigMap: { type: Object, required: false, default: () => ({}) },
  },
  computed: {
    ...mapGetters(['hasValueStoryAccess']),
    deviceSettings() {
      return this.activeDevices[this.device.Serial_number];
    },
    isActive() {
      return Boolean(this.deviceSettings?.active);
    },
    isGuidance() {
      return Boolean(this.deviceSettings?.isInferenceRunning);
    },
    isRecording() {
      return Boolean(this.deviceSettings?.record_shift);
    },
    isIdle() {
      if (!this.isActive) return false;
      return !this.isGuidance && !this.isRecording;
    },
    mode() {
      if (!this.isActive) return 'offline';
      if (this.isRecording) return 'recording';
      if (this.isGuidance) return 'guidance';
      return 'idle';
    },
    modeLabel() {
      return MODE_LABELS[this.mode];
    },
    deviceName() {
      return this.device.display_name || this.device.Serial_number;
    },
    truncatedName() {
      return this.deviceName.length > 22
        ? this.deviceName.slice(0, 22) + '…'
        : this.deviceName;
    },
    currentTaskId() {
      return this.deviceSettings?.Task || null;
    },
    currentTaskName() {
      return this.idToTaskNameMapping[this.currentTaskId] || '';
    },
    statsRow() {
      const key = `${this.device.Serial_number}${this.currentTaskId}`;
      return this.activeDevicesStatus[key] || {};
    },
    cyclesToday() {
      const v = Number(this.statsRow.number_of_cycles);
      return Number.isFinite(v) ? v.toLocaleString() : '';
    },
    hoursRecorded() {
      return formatHoursRecorded(this.statsRow.hours_recorded);
    },
    badPct() {
      const total = Number(this.statsRow.number_of_cycles) || 0;
      const bad = Number(this.statsRow.bad_cycles) || 0;
      if (!total) return '—';
      return `${((bad / total) * 100).toFixed(1)}%`;
    },
    taktGrade() {
      return this.statsRow.takt_grade || '';
    },
    valueStoryConfig() {
      return this.valueStoryConfigMap?.[this.currentTaskId] || null;
    },
    hasValueStoryConfig() {
      const cfg = this.valueStoryConfig;
      if (!cfg) return false;
      const hasSteps = cfg.steps && Object.keys(cfg.steps).length > 0;
      const hasEconomics = cfg.economics && Object.keys(cfg.economics).length > 0;
      return hasSteps || hasEconomics;
    },
    costAvoided() {
      if (!this.hasValueStoryConfig) return 0;
      const bad = Number(this.statsRow.bad_cycles) || 0;
      return roughCostAvoided(this.valueStoryConfig, bad);
    },
    topMissedSteps() {
      const raw = this.statsRow.top_missed_steps;
      if (!Array.isArray(raw)) return [];
      return raw.slice(0, 3);
    },
  },
  methods: {
    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '$0';
      if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
      return `$${Math.round(n).toLocaleString()}`;
    },
    handleCardClick() {
      if (!this.isActive || !this.currentTaskId) return;
      // Match existing WorkStationCard behavior — open Operation Analytics
      // for this device/task in a new tab.
      const { href } = this.$router.resolve({
        name: 'Operation Level Analytics',
        query: {
          taskId: this.currentTaskId,
          deviceId: JSON.stringify([this.device.Serial_number]),
        },
      });
      window.open(href, '_blank');
    },
  },
};
</script>

<style scoped>
.ws-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 12px 14px 12px 18px;
  min-height: 160px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}
.ws-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
  background: #d9d9d9;
}
.ws-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border-color: #91d5ff;
}
.ws-card--guidance::before {
  background: #52c41a;
}
.ws-card--recording::before {
  background: #722ed1;
}
.ws-card--idle::before {
  background: #faad14;
}
.ws-card--offline::before {
  background: #bfbfbf;
}
.ws-card--offline {
  opacity: 0.75;
}

.ws-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.ws-name-block {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}
.ws-name {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-star {
  color: #faad14;
  font-size: 12px;
  flex-shrink: 0;
}
.ws-mode-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  color: #595959;
  white-space: nowrap;
}
.ws-mode-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
}
.ws-card--guidance .ws-mode-chip {
  background: #f6ffed;
  border-color: #b7eb8f;
  color: #237804;
}
.ws-card--recording .ws-mode-chip {
  background: #f9f0ff;
  border-color: #d3adf7;
  color: #531dab;
}
.ws-card--idle .ws-mode-chip {
  background: #fff7e6;
  border-color: #ffd591;
  color: #ad4e00;
}

.ws-body {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}
.ws-primary {
  text-align: center;
}
.ws-primary-value {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
  margin-bottom: 4px;
}
.ws-primary-label {
  font-size: 12px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.ws-primary--muted .ws-primary-value {
  font-size: 20px;
  color: #8c8c8c;
  font-weight: 600;
}

.ws-footer {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ws-task {
  font-size: 12px;
  color: #595959;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-task-name {
  color: #1890ff;
  font-weight: 500;
}
.ws-task-muted {
  color: #bfbfbf;
  font-style: italic;
}
.ws-kpis {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ws-kpi {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 12px;
  background: #fafafa;
  border-radius: 6px;
  padding: 2px 6px;
}
.ws-kpi-value {
  font-weight: 700;
  color: #1a1a1a;
}
.ws-kpi-label {
  color: #8c8c8c;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.3px;
}
.ws-kpi--roi {
  background: #f6ffed;
}
.ws-kpi--roi .ws-kpi-value {
  color: #237804;
}

.ws-missed {
  margin-top: 6px;
  border-top: 1px dashed #f0f0f0;
  padding-top: 6px;
}
.ws-missed-label {
  font-size: 10px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.ws-missed-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #595959;
  padding: 2px 0;
}
.ws-missed-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: #fff2e8;
  color: #ad4e00;
  font-weight: 700;
  font-size: 10px;
  flex-shrink: 0;
}
.ws-missed-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-missed-count {
  font-weight: 700;
  color: #ad4e00;
}
</style>
